import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { isDefined } from '@freelancer/datastore';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import * as Rx from 'rxjs';
import { delay, filter, tap } from 'rxjs/operators';
import { FinopsMessagingService } from '../../common/finops-messaging/finops-messaging.service';
import {
  FinopsEventType,
  FinopsOverlay,
  OverlayTypes,
} from '../../common/finops-messaging/finops-messaging.types';

@Component({
  selector: 'app-finops-overlay-title',
  template: `
    <fl-heading
      [size]="HeadingSize.MID"
      [headingType]="HeadingType.H3"
      [flMarginBottom]="Margin.XSMALL"
    >
      {{ title }}
    </fl-heading>
  `,
})
export class FinopsOverlayTitleComponent {
  Margin = Margin;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;

  @Input() title: string;
}

@Component({
  selector: 'app-finops-overlay-body',
  template: `
    <fl-text [flMarginBottom]="Margin.XSMALL"> {{ body }} </fl-text>
  `,
})
export class FinopsOverlayBodyComponent {
  Margin = Margin;

  @Input() body: string;
}

@Component({
  selector: 'app-finops-overlay-button',
  template: `
    <fl-button
      flTrackingLabel="closeOverLay"
      [color]="ButtonColor.SECONDARY"
      [size]="ButtonSize.LARGE"
    >
      {{ cta }}
    </fl-button>
  `,
})
export class FinopsOverlayButtonComponent {
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;

  @Input() cta: string;
}

@Component({
  selector: 'app-finops-overlay',
  template: `
    <fl-bit>
      <fl-bit class="OverlayContainer" *ngIf="overlay">
        <fl-icon
          class="CloseButton"
          *ngIf="overlay.onClose"
          [name]="'ui-close'"
          [size]="IconSize.MID"
          (click)="closeOverlay()"
        ></fl-icon>

        <fl-bit class="OverlayCard">
          <fl-picture
            *ngIf="overlay.brand"
            [alt]="overlay.brand.alt"
            [src]="overlay.brand.src"
          ></fl-picture>
          <ng-container [ngSwitch]="overlay.overlayType">
            <ng-container *ngSwitchCase="OverlayTypes.BLOCKING_ERROR">
              <app-finops-overlay-title
                i18n-title="Overlay title"
                title="Oops! Something went wrong!"
              ></app-finops-overlay-title>
              <app-finops-overlay-body
                i18n-body="Overlay body"
                body="Please try refreshing the page. Please contact support if the issue persists."
              ></app-finops-overlay-body>
              <app-finops-overlay-button
                i18n-cta="Overlay button cta"
                cta="Refresh"
                (click)="onAction()"
              ></app-finops-overlay-button>
            </ng-container>

            <ng-container *ngSwitchCase="OverlayTypes.CC_3DS">
              <app-finops-overlay-body
                i18n-body="Overlay body"
                body="Your bank requires additional verifications. You will be shown a new window to complete the transaction."
              ></app-finops-overlay-body>
              <app-finops-overlay-button
                i18n-cta="Overlay button cta"
                cta="Show the window"
                (click)="onAction()"
              ></app-finops-overlay-button>
            </ng-container>

            <ng-container *ngSwitchCase="OverlayTypes.CC_CAPTCHA">
              <app-finops-overlay-body
                i18n-body="Overlay body"
                body="Please resolve the security challenge for credit/debit card payments"
              ></app-finops-overlay-body>
              <app-finops-overlay-button
                i18n-cta="Overlay button cta"
                cta="Show the window"
                (click)="onAction()"
              ></app-finops-overlay-button>
            </ng-container>

            <ng-container *ngSwitchCase="OverlayTypes.DEPOSIT_SUCCESS">
              <app-finops-overlay-title
                i18n-title="Overlay title"
                title="Payment Successful!"
              ></app-finops-overlay-title>
              <app-finops-overlay-body
                i18n-body="Overlay body"
                body="You will be redirected in 5 seconds."
              ></app-finops-overlay-body>
              <app-finops-overlay-button
                i18n-cta="Overlay button cta"
                cta="Continue"
                (click)="onAction()"
              ></app-finops-overlay-button>
            </ng-container>

            <ng-container *ngSwitchCase="OverlayTypes.EXTERNAL_POPUP">
              <app-finops-overlay-body
                i18n-body="Overlay body"
                body="Can't see the secure payment window?"
              ></app-finops-overlay-body>
              <app-finops-overlay-button
                i18n-cta="Overlay button cta"
                cta="Show the window"
                (click)="onAction()"
              ></app-finops-overlay-button>
            </ng-container>

            <ng-container *ngSwitchCase="OverlayTypes.VERIFICATION_SUCCESS">
              <app-finops-overlay-title
                i18n-title="Overlay title"
                title="Verification Successful!"
              ></app-finops-overlay-title>
              <app-finops-overlay-body
                i18n-body="Overlay body"
                body="You will be redirected in 5 seconds."
              ></app-finops-overlay-body>
              <app-finops-overlay-button
                i18n-cta="Overlay button cta"
                cta="Continue"
                (click)="onAction()"
              ></app-finops-overlay-button>
            </ng-container>
          </ng-container>
        </fl-bit>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./finops-overlay.component.scss'],
})
export class FinopsOverlayComponent implements OnDestroy, OnInit {
  IconSize = IconSize;
  OverlayTypes = OverlayTypes;

  @Output() overlayOn = new EventEmitter<boolean>();

  private subscriptions: ReadonlyArray<Rx.Subscription> = [];
  overlay?: FinopsOverlay;

  constructor(
    private finopsMessaging: FinopsMessagingService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    const closeEvents$ = this.finopsMessaging.getEventStream().pipe(
      filter(
        closeEvent => closeEvent.eventType === FinopsEventType.CLOSE_OVERLAY,
      ),
      tap(() => this.closeOverlay()),
    );
    const overlay$ = this.finopsMessaging.getOverlayStream().pipe(
      filter(isDefined),
      delay(0),
    );

    this.subscriptions = [
      closeEvents$.subscribe(),
      overlay$.subscribe(overlay => this.openOverlay(overlay)),
    ];
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private openOverlay(overlay: FinopsOverlay) {
    this.overlay = overlay;
    this.cdr.detectChanges();
    this.overlayOn.emit(true);
  }

  closeOverlay() {
    if (this.overlay && this.overlay.onClose) {
      this.overlay.onClose();
    }
    this.overlay = undefined;
    this.cdr.detectChanges();
    this.overlayOn.emit(false);
  }

  onAction() {
    if (this.overlay) {
      this.overlay.ctaAction();
    }
  }
}
